import React, { useState } from 'react';
import { ChevronDownIcon, QuoteIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Section, SectionHeading } from '../ui/Section';
import { Reveal } from '../ui/Reveal';
import { TrustRating } from '../ui/TrustRating';
import { featuredTestimonials, moreTestimonials } from '../../data/testimonials';
import type { Testimonial } from '../../data/testimonials';
function initials(name: string): string {
  return name.split(' ').map(part => part.charAt(0)).join('').slice(0, 2).toUpperCase();
}
function TestimonialCard({
  testimonial
}: {
  testimonial: Testimonial;
}) {
  return <figure className="flex h-full flex-col rounded-2xl bg-white p-6 shadow-card transition-[transform,box-shadow] duration-200 ease-smooth hover:-translate-y-0.5 hover:shadow-lift sm:p-7" data-id="element-333" data-mp="8zt8fl2vxsf3">
      <QuoteIcon className="h-5 w-5 shrink-0 text-gold" aria-hidden="true" data-id="element-334" data-mp="2av3kdxe5p1eo" />
      <blockquote className="mt-4 text-[0.95rem] leading-relaxed text-ink sm:text-base" data-id="element-335" data-mp="2ft548kjep4r7">
        “{testimonial.quote}”
      </blockquote>
      <figcaption className="mt-auto flex items-center gap-3 pt-6" data-id="element-336" data-mp="1smdq7tsqaxqm">
        {testimonial.photo ? <img src={testimonial.photo} alt={`Retrato de ${testimonial.name}`} className="h-12 w-12 shrink-0 rounded-full object-cover" width={96} height={96} loading="lazy" data-id="element-337" data-mp="1nrpxtjultj6r" /> : <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-cream text-sm font-bold text-forest" aria-hidden="true" data-id="element-338" data-mp="1eufmjyhsdu1u">
            {initials(testimonial.name)}
          </span>}
        <div className="min-w-0" data-id="element-339" data-mp="26ktvdgay0g6n">
          <p className="truncate text-sm font-bold text-forest" data-id="element-340" data-mp="26ihhpfc7wrze">{testimonial.name}</p>
          <p className="truncate text-xs text-muted" data-id="element-341" data-mp="22ktg5tg0s0yj">
            {testimonial.role} · {testimonial.location}
          </p>
        </div>
        <span className="ml-auto hidden shrink-0 rounded-full bg-cream px-3 py-1 text-[0.7rem] font-semibold text-forest sm:block" data-id="element-342" data-mp="r436u1gaut0p">
          {testimonial.highlight}
        </span>
      </figcaption>
    </figure>;
}
export function Testimonials() {
  const [expanded, setExpanded] = useState(false);
  const reduceMotion = useReducedMotion();
  return <Section className="bg-cream" aria-labelledby="testimonios-titulo" data-id="element-343" data-mp="sk7afav4m114">
      <Reveal data-id="element-344" data-mp="ejwgu02fm8c9">
        <TrustRating className="!justify-start" data-id="element-345" data-mp="ekekvivc5quq" />
        <SectionHeading id="testimonios-titulo" className="mt-5" data-id="element-346" data-mp="im9l0k1j5y6x">
          Mujeres reales, tardes distintas
        </SectionHeading>
        <p className="mt-3 text-sm text-muted" data-id="element-347" data-mp="4u3elpo6j5ko">
          Experiencias de mujeres que aplicaron el protocolo
        </p>
      </Reveal>

      <ul className="mt-8 grid gap-5 md:grid-cols-2" data-id="element-348" data-mp="9nxwcnk85ah4">
        {featuredTestimonials.map((testimonial, index) => <Reveal as="li" key={testimonial.name} delay={index * 0.06} className="list-none" data-id="element-349" data-mp="3r09wfo0wcxi3" data-mpi={index}>
            <TestimonialCard testimonial={testimonial} data-id="element-350" data-mp="3vzd137cyt1pj" />
          </Reveal>)}
      </ul>

      <AnimatePresence initial={false} data-id="element-351" data-mp="3i2gan70rdm96">
        {expanded && <motion.ul id="mas-testimonios" className="grid gap-5 overflow-hidden md:grid-cols-2" initial={reduceMotion ? undefined : {
        height: 0,
        opacity: 0
      }} animate={reduceMotion ? undefined : {
        height: 'auto',
        opacity: 1,
        marginTop: 20
      }} exit={reduceMotion ? undefined : {
        height: 0,
        opacity: 0,
        marginTop: 0
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-352" data-mp="3n19cu3alhqu1">
            {moreTestimonials.map((testimonial, index) => <motion.li key={testimonial.name} className="list-none" initial={reduceMotion ? undefined : {
          opacity: 0,
          y: 14
        }} animate={reduceMotion ? undefined : {
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.26,
          delay: Math.min(index, 8) * 0.035,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-353" data-mp="3mcis37yktc3o" data-mpi={index}>
                <TestimonialCard testimonial={testimonial} data-id="element-354" data-mp="8ydym9inla06" />
              </motion.li>)}
          </motion.ul>}
      </AnimatePresence>

      <div className="mt-7 flex justify-center" data-id="element-355" data-mp="41f9k5dujt8u">
        <button type="button" onClick={() => setExpanded(value => !value)} aria-expanded={expanded} aria-controls="mas-testimonios" className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-forest bg-white px-7 py-3.5 text-sm font-semibold text-forest transition-[transform,background-color,color] duration-200 ease-smooth hover:-translate-y-0.5 hover:bg-forest hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold sm:w-auto" data-id="element-356" data-mp="1ygaxci7g8sdu">
          {expanded ? 'Ver menos testimonios' : `Ver más testimonios (${moreTestimonials.length})`}
          <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-smooth ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-357" data-mp="1ogtu07fo6ql1" />
        </button>
      </div>

      <Reveal delay={0.1} data-id="element-358" data-mp="2zxaam42ynd9h">
        <p className="mt-7 text-xs leading-relaxed text-faint" data-id="element-359" data-mp="2zw0ehkfo4f5h">
          Testimonios de experiencias individuales. Los resultados varían según cada persona y su
          aplicación del protocolo.
        </p>
      </Reveal>
    </Section>;
}